import { useRef } from 'react';
import { m, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import HexShape from './HexShape';

const services = [
  {
    num: '01',
    name: 'Custom software',
    desc: 'Bespoke tools built around how your business actually operates. Not a SaaS subscription that almost fits. Something made entirely for you.',
  },
  {
    num: '02',
    name: 'Web portals and tools',
    desc: 'Client portals, booking systems, management dashboards. Digital infrastructure built for the way your team and customers work.',
  },
  {
    num: '03',
    name: 'Automations',
    desc: 'Connect your tools. Remove the repetitive manual work. Let technology handle the parts that do not need a human in the loop.',
  },
  {
    num: '04',
    name: 'Digital presence',
    desc: 'Websites that represent you properly: fast, accessible, beautifully designed, and genuinely yours.',
  },
];

export default function Services() {
  const shouldReduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const mouseX = useMotionValue(50);
  const springX = useSpring(mouseX, { stiffness: 40, damping: 18 });

  const handleMouseMove = (e) => {
    if (shouldReduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set(((e.clientX - r.left) / r.width) * 100);
  };

  const h1y = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const h2y = useTransform(scrollYProgress, [0, 1], [0, -35]);
  const h3y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const h1x = useTransform(springX, [0, 100], [-14, 14]);
  const h2x = useTransform(springX, [0, 100], [10, -10]);
  const h3x = useTransform(springX, [0, 100], [-8, 8]);

  const hexes = [
    { size: 440, left: '-7%', top: '8%',   dur: 22, delay: 0,   x: h1x, y: h1y },
    { size: 280, left: '78%', top: '-14%', dur: 28, delay: 3.5, x: h2x, y: h2y },
    { size: 210, left: '63%', top: '58%',  dur: 19, delay: 1.5, x: h3x, y: h3y },
  ];

  return (
    <section
      ref={ref}
      className="bg-primary-dark py-28 sm:py-36 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        {hexes.map((hex, i) => (
          <m.div
            key={i}
            className="absolute"
            style={{ left: hex.left, top: hex.top, x: hex.x, y: hex.y }}
          >
            <m.div
              animate={shouldReduce ? undefined : {
                y: [0, -18, 0],
                x: [0, 9, 0],
                rotate: [0, 360],
              }}
              transition={{
                y: { duration: hex.dur, repeat: Infinity, ease: 'easeInOut', delay: hex.delay },
                x: { duration: hex.dur, repeat: Infinity, ease: 'easeInOut', delay: hex.delay },
                rotate: { duration: hex.dur * 4, ease: 'linear', repeat: Infinity, delay: 0 },
              }}
            >
              <HexShape size={hex.size} className="text-primary-light" style={{ opacity: 0.13 }} />
            </m.div>
          </m.div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <m.div
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="text-primary-light/50 text-xs tracking-[0.2em] uppercase font-medium mb-4">
            What we build
          </p>
          <h2
            className="text-white font-extrabold leading-[0.93] tracking-tight"
            style={{ fontSize: 'clamp(2.25rem, 4.5vw, 4rem)' }}
          >
            Digital products that fit.
          </h2>
        </m.div>

        <div>
          {services.map((service, i) => (
            <m.div
              key={service.num}
              initial={shouldReduce ? false : { opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, delay: shouldReduce ? 0 : i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              whileHover={shouldReduce ? undefined : { x: 3, transition: { type: 'spring', stiffness: 380, damping: 28 } }}
              className="border-t border-white/10 py-8 grid grid-cols-1 md:grid-cols-[2.5rem_1fr_1fr] gap-3 md:gap-8 items-start group"
            >
              <span className="text-primary-light/40 text-[0.7rem] tracking-widest font-medium pt-1" aria-hidden="true">
                {service.num}
              </span>
              <h3 className="text-white font-semibold text-xl group-hover:text-accent transition-colors duration-300">
                {service.name}
              </h3>
              <p className="text-white/50 leading-relaxed tracking-[0.01em] text-base">{service.desc}</p>
            </m.div>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </div>
    </section>
  );
}
